/**
 * Full-page Add Case Log — replaces the CaseDetail.js "Add Log" dialog.
 * Route: /cases/:caseId/logs/new
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, ClipboardList, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

export default function LogFormPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);

  const [caseInfo, setCaseInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time_start: '09:00', time_end: '',
    action: '', category: '', description: '',
    location_address: '', city: '', state: '',
    narrative: '', activity_observed: '', additional_notes: '',
  });
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setCaseInfo).catch(() => {});
  }, [caseId, token]);

  const goBack = () => navigate(`/cases/${caseId}?tab=logs`);

  const submit = async () => {
    if (!form.narrative?.trim()) {
      toast({ title: 'Missing narrative', description: 'Narrative is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, case_id: caseId }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Save failed (${res.status})`);
      toast({ title: 'Case log added', description: `${form.date} ${form.time_start}` });
      goBack();
    } catch (e) {
      toast({ title: 'Could not add log', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="log-form-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="log-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-amber-300" /> Add Case Log Entry
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-slate-300">Date *</Label>
            <Input type="date" value={form.date} onChange={e => setF('date', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" data-testid="log-date-input" />
          </div>
          <div>
            <Label className="text-slate-300">Start</Label>
            <Input type="time" value={form.time_start} onChange={e => setF('time_start', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" />
          </div>
          <div>
            <Label className="text-slate-300">End</Label>
            <Input type="time" value={form.time_end} onChange={e => setF('time_end', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300">Action</Label>
            <Select value={form.action || 'none'} onValueChange={v => setF('action', v === 'none' ? '' : v)}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="log-action-select">
                <SelectValue placeholder="Select action..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Select —</SelectItem>
                <SelectItem value="SURVEILLANCE">Surveillance</SelectItem>
                <SelectItem value="INTERVIEW">Interview</SelectItem>
                <SelectItem value="RECORDS_SEARCH">Records Search</SelectItem>
                <SelectItem value="EVIDENCE_COLLECTION">Evidence Collection</SelectItem>
                <SelectItem value="SKIP_TRACE">Skip Trace</SelectItem>
                <SelectItem value="STAKEOUT">Stakeout</SelectItem>
                <SelectItem value="FOLLOW_UP">Follow-Up</SelectItem>
                <SelectItem value="COURT_APPEARANCE">Court Appearance</SelectItem>
                <SelectItem value="REPORT_WRITING">Report Writing</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Category</Label>
            <Select value={form.category || 'none'} onValueChange={v => setF('category', v === 'none' ? '' : v)}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="log-category-select">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Select —</SelectItem>
                <SelectItem value="FIELD_WORK">Field Work</SelectItem>
                <SelectItem value="OFFICE_WORK">Office Work</SelectItem>
                <SelectItem value="LEGAL">Legal</SelectItem>
                <SelectItem value="CLIENT_CONTACT">Client Contact</SelectItem>
                <SelectItem value="ADMINISTRATIVE">Administrative</SelectItem>
                <SelectItem value="TRAVEL">Travel</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-slate-300">Description</Label>
          <Input value={form.description} onChange={e => setF('description', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white"
            placeholder="Brief description of this entry" data-testid="log-description-input" />
        </div>
        <div>
          <Label className="text-slate-300">Location</Label>
          <Input value={form.location_address} onChange={e => setF('location_address', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" placeholder="Street address" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300">City</Label>
            <Input value={form.city} onChange={e => setF('city', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" />
          </div>
          <div>
            <Label className="text-slate-300">State</Label>
            <Input value={form.state} onChange={e => setF('state', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" />
          </div>
        </div>
        <div>
          <Label className="text-slate-300">Activity Observed</Label>
          <Textarea value={form.activity_observed} onChange={e => setF('activity_observed', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={2} />
        </div>
        <div>
          <Label className="text-slate-300">Narrative *</Label>
          <Textarea value={form.narrative} onChange={e => setF('narrative', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={5}
            placeholder="Detailed narrative of activities..." data-testid="log-narrative-input" />
        </div>
        <div>
          <Label className="text-slate-300">Additional Notes</Label>
          <Textarea value={form.additional_notes} onChange={e => setF('additional_notes', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={2} />
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="log-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="log-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving || !form.narrative?.trim()}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-60"
          data-testid="submit-log-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : 'Add Log Entry'}
        </Button>
      </div>
    </div>
  );
}
